'use client';

import { useState, useRef, useEffect } from 'react';
import gsap from 'gsap';

import { Link } from '../../../containers/PageBuilder/Primitives/Link';
import css from './ScrollDown.module.css';

/**
 * Hover area that shows the frosted "Scroll" cursor bubble.
 *
 * The overlay covers the section to track the pointer, which also means it
 * intercepts clicks. Pass `href` when the section is meant to be clickable and
 * the overlay itself becomes the link.
 */
export default function ScrollDown({ nextSectionId, href, linkLabel }) {
  const [isMobile, setIsMobile] = useState(false);

  const sectionRef = useRef(null);
  const cursorRef = useRef(null);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 600);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);

    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  useEffect(() => {
    if (!cursorRef.current) return;

    gsap.set(cursorRef.current, {
      xPercent: -50,
      yPercent: -50,
      opacity: 0,
      scale: 0.8,
    });
  }, []);

  const handleMouseMove = e => {
    if (!cursorRef.current) return;

    gsap.to(cursorRef.current, {
      x: e.clientX,
      y: e.clientY,
      duration: 0.35,
      delay: 0.15,
      ease: 'power1.out',
      overwrite: 'auto',
    });
  };

  const handleMouseEnter = () => {
    if (!cursorRef.current) return;

    gsap.killTweensOf(cursorRef.current);

    gsap.to(cursorRef.current, {
      opacity: 1,
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleMouseLeave = () => {
    if (!cursorRef.current) return;

    gsap.killTweensOf(cursorRef.current);

    gsap.to(cursorRef.current, {
      opacity: 0,
      scale: 0.8,
      duration: 0.2,
      ease: 'power2.out',
    });
  };

  // NOTE: DESIGNER want user to scroll itself
  // const handleScrollToNextSection = () => {
  //   const nextSection = document.getElementById(nextSectionId);
  //   if (nextSection) {
  //     nextSection.scrollIntoView({ behavior: 'smooth' });
  //   }
  // };

  if (isMobile) return null;

  return (
    <>
      <div
        ref={sectionRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        // onClick={handleScrollToNextSection}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
        }}
      >
        {href ? (
          <Link href={href} className={css.overlayLink}>
            <span className={css.overlayLinkLabel}>{linkLabel || 'Open'}</span>
          </Link>
        ) : null}
      </div>

      <div
        ref={cursorRef}
        className="customCursor"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 9999,
          willChange: 'transform, opacity',
        }}
      >
        Scroll
      </div>
    </>
  );
}